/**
 * AssetLoader — JSON-only for Phase 1.
 *
 * All gameplay content lives in JSON files under /src/data. Vite resolves
 * `?url` imports at build time; we fetch the resulting URLs at boot. We do
 * not ship any bitmap assets yet — every entity is a Pixi Graphics object.
 *
 * If you later add textures, load them here and expose via `getTexture(id)`.
 */

import foodsUrl from '@/data/foods.json?url';
import mutationsUrl from '@/data/mutations.json?url';
import upgradesUrl from '@/data/upgrades.json?url';
import predatorsUrl from '@/data/predators.json?url';
import environmentalEventsUrl from '@/data/environmentalEvents.json?url';
import codexUrl from '@/data/codex.json?url';
import achievementsUrl from '@/data/achievements.json?url';
import metaUpgradesUrl from '@/data/metaUpgrades.json?url';

import type { FoodDef } from '@/data/types';
import type { MutationDef, MutationFamily } from '@/data/types';
import type { UpgradeDef } from '@/data/types';
import type { PredatorDef } from '@/data/types';
import type { EnvironmentalEventDef } from '@/data/types';
import type { CodexEntry } from '@/data/types';
import type { AchievementDef, MetaUpgradeDef } from '@/data/types';

export interface GameData {
  foods: Record<string, FoodDef>;
  mutations: Record<string, MutationDef>;
  mutationFamilies: Record<string, MutationFamily>;
  upgrades: Record<string, UpgradeDef>;
  predators: Record<string, PredatorDef>;
  events: Record<string, EnvironmentalEventDef>;
  codex: Record<string, CodexEntry>;
  achievements: Record<string, AchievementDef>;
  metaUpgrades: Record<string, MetaUpgradeDef>;
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load ${url}: ${res.status}`);
  return res.json() as Promise<T>;
}

function indexById<T extends { id: string }>(arr: T[]): Record<string, T> {
  const out: Record<string, T> = {};
  for (const item of arr) out[item.id] = item;
  return out;
}

export async function loadGameData(): Promise<GameData> {
  const [foods, mutationsFile, upgrades, predators, events, codex, achievements, metaUpgrades] = await Promise.all([
    fetchJson<FoodDef[]>(foodsUrl),
    fetchJson<{ families: MutationFamily[]; mutations: MutationDef[] }>(mutationsUrl),
    fetchJson<UpgradeDef[]>(upgradesUrl),
    fetchJson<PredatorDef[]>(predatorsUrl),
    fetchJson<EnvironmentalEventDef[]>(environmentalEventsUrl),
    fetchJson<CodexEntry[]>(codexUrl),
    fetchJson<AchievementDef[]>(achievementsUrl),
    fetchJson<MetaUpgradeDef[]>(metaUpgradesUrl),
  ]);

  return {
    foods: indexById(foods),
    mutations: indexById(mutationsFile.mutations),
    mutationFamilies: indexById(mutationsFile.families),
    upgrades: indexById(upgrades),
    predators: indexById(predators),
    events: indexById(events),
    codex: indexById(codex),
    achievements: indexById(achievements),
    metaUpgrades: indexById(metaUpgrades),
  };
}
